import { Splide } from '@splidejs/splide';
import '@splidejs/splide/css';

function pauseVideo(video) {
    if (!video) return;
    video.pause();
}

function loadAndPlay(video) {
    if (!video) return;
    if (!video.src) {
        // First time loading - set src and wait for data
        video.src = video.dataset.src;
        video.load();
        video.addEventListener('loadeddata', () => {
            video.play().catch(() => {});
        }, { once: true });
    } else if (video.readyState >= 2) {
        // Video already loaded - just resume playback
        video.play().catch(() => {});
    } else {
        // Video src is set but not loaded yet - wait for it
        video.addEventListener('loadeddata', () => {
            video.play().catch(() => {});
        }, { once: true });
    }
}

// Global mute state - shared across all sliders
let globalMuteState = true;

function initUgcSliders() {
    const sliders = document.querySelectorAll('.js-ugc-slider');

    sliders.forEach((sliderElement) => {
        const prevButton = sliderElement.parentElement.querySelector('.ugc-slider__prev');
        const nextButton = sliderElement.parentElement.querySelector('.ugc-slider__next');
        const muteButtons = sliderElement.querySelectorAll('.js-ugc-mute-toggle');

        const splide = new Splide(sliderElement, {
            type: 'loop',
            perPage: 5,
            perMove: 1,
            focus: 'center',
            pagination: false,
            autoplay: false,
            arrows: false,
            gap: '0px',
            drag: false,
            padding: {
                left: '0px',
                right: '0px',
            },
            breakpoints: {
                992: {
                    perPage: 3.5,
                },
                768: {
                    perPage: 1.5,
                },
            },
        });

        // Update mute state for all videos and buttons in this slider
        function updateMuteState() {
            const allVideos = sliderElement.querySelectorAll('video');
            
            allVideos.forEach((video) => {
                video.muted = globalMuteState;
            });
            
            muteButtons.forEach((button) => {
                const mutedIcon = button.querySelector('.ugc-slider__mute-icon--muted');
                const unmutedIcon = button.querySelector('.ugc-slider__mute-icon--unmuted');

                if (globalMuteState) {
                    mutedIcon.style.display = 'block';
                    unmutedIcon.style.display = 'none';
                } else {
                    mutedIcon.style.display = 'none';
                    unmutedIcon.style.display = 'block';
                }
            });
        }

        // Handle mute toggle clicks
        muteButtons.forEach((button) => {
            button.addEventListener('click', (e) => {
                globalMuteState = !globalMuteState;

                // Update all sliders
                document.querySelectorAll('.js-ugc-slider').forEach((slider) => {
                    const videos = slider.querySelectorAll('video');

                    videos.forEach((video) => {
                        video.muted = globalMuteState;
                    });

                    const buttons = slider.querySelectorAll('.js-ugc-mute-toggle');
                    
                    buttons.forEach((btn) => {
                        const mutedIcon = btn.querySelector('.ugc-slider__mute-icon--muted');
                        const unmutedIcon = btn.querySelector('.ugc-slider__mute-icon--unmuted');

                        if (globalMuteState) {
                            mutedIcon.style.display = 'block';
                            unmutedIcon.style.display = 'none';
                        } else {
                            mutedIcon.style.display = 'none';
                            unmutedIcon.style.display = 'block';
                        }
                    });
                });
            });
        });

        splide.on('move', (newIndex, prevIndex) => {
            const prevActiveSlide = splide.Components.Slides.getAt(prevIndex);
            const newActiveSlide = splide.Components.Slides.getAt(newIndex);

            if (prevActiveSlide) {
                const prevVideo = prevActiveSlide.slide.querySelector('video');
                const prevItem = prevActiveSlide.slide.querySelector('.ugc-slider__item');

                if (prevVideo) {
                    pauseVideo(prevVideo);
                }

                if (prevItem) {
                    prevItem.classList.remove('is-active');
                }
            }

            if (newActiveSlide) {
                const newVideo = newActiveSlide.slide.querySelector('video');
                const newItem = newActiveSlide.slide.querySelector('.ugc-slider__item');

                if (newVideo) {
                    // Apply current mute state before playing
                    newVideo.muted = globalMuteState;
                    loadAndPlay(newVideo);
                }

                if (newItem) {
                    newItem.classList.add('is-active');
                }
            }
        });

        // Prevent non-active videos from playing
        sliderElement.addEventListener('play', (e) => {
            if (e.target.classList.contains('js-ugc-slider-video')) {
                const activeSlide = splide.Components.Slides.getAt(splide.index);
                const activeVideo = activeSlide?.slide.querySelector('video');
                if (e.target !== activeVideo) {
                    e.target.pause();
                }
            }
        }, true);

        splide.mount();

        // Custom drag handler - always moves one item based on direction
        const track = sliderElement.querySelector('.splide__track');
        let startX = 0;
        let isDragging = false;

        const getClientX = (e) => (e.touches || e.changedTouches) ? (e.touches?.[0] || e.changedTouches[0]).clientX : e.clientX;

        track.addEventListener('touchstart', (e) => {
            startX = getClientX(e);
            isDragging = true;
        }, { passive: false });

        track.addEventListener('touchmove', (e) => {
            if (isDragging && Math.abs(getClientX(e) - startX) > 10) {
                e.preventDefault();
            }
        }, { passive: false });

        track.addEventListener('touchend', (e) => {
            if (!isDragging) return;
            isDragging = false;
            const diffX = getClientX(e) - startX;
            if (Math.abs(diffX) > 50) {
                splide.go(diffX > 0 ? '<' : '>');
            }
        }, { passive: false });

        track.addEventListener('mousedown', (e) => {
            startX = getClientX(e);
            isDragging = true;
        });

        track.addEventListener('mousemove', (e) => {
            if (isDragging && Math.abs(getClientX(e) - startX) > 10) {
                e.preventDefault();
            }
        });

        track.addEventListener('mouseup', (e) => {
            if (!isDragging) return;
            isDragging = false;
            const diffX = getClientX(e) - startX;
            if (Math.abs(diffX) > 50) {
                splide.go(diffX > 0 ? '<' : '>');
            }
        });

        track.addEventListener('mouseleave', () => {
            isDragging = false;
        });

        // Connect custom arrow buttons to Splide navigation
        if (prevButton && nextButton) {
            prevButton.addEventListener('click', () => splide.go('<'));
            nextButton.addEventListener('click', () => splide.go('>'));
        }

        // Handle click on slides to center them (works for original and clones)
        splide.on('click', (Slide, e) => {
            if (e.target.closest('.ugc-slider__product-link')) return;
            if (e.target.closest('.js-ugc-product-slider .splide__arrow')) return;
            
            splide.go(Slide.index);
        });

        // Initialize active state for the first slide
        const firstActiveSlide = splide.Components.Slides.getAt(splide.index);
        if (firstActiveSlide) {
            const video = firstActiveSlide.slide.querySelector('video');

            if (video) {
                // Apply current mute state
                video.muted = globalMuteState;
                loadAndPlay(video);

                const item = firstActiveSlide.slide.querySelector('.ugc-slider__item');

                if (item) {
                    item.classList.add('is-active');
                }
            }
        }
        
        // Initialize mute button states
        updateMuteState();
    });
}

function initProductSliders() {
    const productSliders = document.querySelectorAll('.js-ugc-product-slider');

    productSliders.forEach((sliderElement) => {
        const slides = sliderElement.querySelectorAll('.splide__slide');
        const slideCount = slides.length;

        const splide = new Splide(sliderElement, {
            type: 'loop',
            perPage: 1,
            perMove: 1,
            pagination: false,
            autoplay: false,
            arrows: slideCount > 1, // Only show arrows if there are multiple products
            gap: '0px',
            drag: false,
        });

        splide.mount();

        // Prevent arrow clicks from bubbling to main slider
        const arrows = sliderElement.querySelectorAll('.splide__arrow');
        arrows.forEach((arrow) => {
            arrow.addEventListener('click', (e) => {
                e.stopPropagation();
            });
        });
    });
}

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
        initUgcSliders();
        initProductSliders();
    });
} else {
    initUgcSliders();
    initProductSliders();
}
